import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button, Modal } from '../../';
import { Checkbox, Alert, Table, Typography, Space, Row, Col, Input } from 'antd';
import { TargetCloudSelector } from '../../../components';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useBulkCreateSourcesMutation } from '../../../api';
import { useGetCloudAccountsQuery } from '../../../api';
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { handleError } from '../../../lib/helpers';
import { processCloudAccounts } from '../../MigrationReadinessDialog/utils';
import { parseCSVToArrays, parseSourceFromCSVRow, setCredentialsStorage } from '../../../lib/csvUtils';
import './UploadMultipleSourcesModal.scss';

const { Text, Link } = Typography;

const MAX_ASSETS = 100;

const UploadMultipleSourcesModal = ({ open, onClose, onSuccess, userOrg }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [sources, setSources] = useState([]);
    const [errors, setErrors] = useState([]);
    const [bulkErrors, setBulkErrors] = useState(null);
    const [loading, setLoading] = useState(false);
    const [overwriteExisting, setOverwriteExisting] = useState(false);
    const [selectedTargetCloud, setSelectedTargetCloud] = useState('');
    const [downloadCSV, setDownloadCSV] = useState(false);
    const [showAllErrors, setShowAllErrors] = useState(false);

    const fileInputRef = useRef(null);
    const { notifySuccess, notifyError } = useNotificationContext();

    // Fetch cloud accounts
    const { data: cloudAccountsData, isLoading: isLoadingCloudAccounts } = useGetCloudAccountsQuery({
        userOrg,
        size: 100,
        sort: 'name,asc',
    });

    // Bulk create sources mutation
    const [bulkCreateSources] = useBulkCreateSourcesMutation();

    // Process cloud accounts using the common utility (for OS-based migrations)
    const processedCloudAccounts = processCloudAccounts(cloudAccountsData, false);

    const handleBrowseClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileSelect = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Reset previous state
        setSources([]);
        setErrors([]);
        setBulkErrors(null);

        // Check file type
        if (!file.name.toLowerCase().endsWith('.csv')) {
            setErrors(['Please select a CSV file.']);
            setSelectedFile(null);
            setFileName('');
            return;
        }

        setSelectedFile(file);
        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                parseCSVContent(e.target.result, file.name);
            } catch (error) {
                setErrors(['Error reading file. Please ensure it is a valid CSV file.']);
                setSelectedFile(null);
                setFileName('');
            }
        };

        reader.readAsText(file);
    }, []);

    const parseCSVContent = useCallback((csvText, filename) => {
        try {
            // Parse CSV using utility that replicates Backbone behavior
            const csvArr = parseCSVToArrays(csvText);

            if (csvArr.length < 2) {
                setErrors(['CSV file must contain at least one data row besides the header.']);
                return;
            }

            // Split header row from data rows (Backbone behavior)
            const headerRow = csvArr[0];
            const dataRows = csvArr.slice(1);

            const parsedSources = [];
            const parseErrors = [];

            // Process each data row (limited to MAX_ASSETS)
            for (let idx = 0; idx < dataRows.length && parsedSources.length < MAX_ASSETS; idx++) {
                const row = dataRows[idx];

                // Skip completely empty rows
                if (row.every((cell) => !cell || !cell.trim())) {
                    continue;
                }

                try {
                    // Parse source using the utility function that replicates Backbone logic
                    let source = parseSourceFromCSVRow(headerRow, row, filename, idx);

                    // Set credentials storage
                    source = setCredentialsStorage(source);

                    // Validate required fields (host/IP is required)
                    if (!source.host && !source.source_ip) {
                        parseErrors.push(`Row ${idx + 2}: Host/IP address is required`);
                        continue;
                    }

                    parsedSources.push(source);
                } catch (error) {
                    parseErrors.push(`Row ${idx + 2}: Error parsing row - ${error.message}`);
                }
            }

            if (parseErrors.length > 0) {
                setErrors(parseErrors);
            } else {
                setErrors([]);
            }

            setSources(parsedSources);
            setBulkErrors(null);
        } catch (error) {
            console.error('CSV parsing error:', error);
            setErrors(['One or more errors parsing the CSV file.']);
            setSources([]);
        }
    }, []);

    const handleTargetCloudChange = useCallback(
        (value) => {
            setSelectedTargetCloud(value);
            if (value && downloadCSV) {
                // If target cloud is selected and download CSV is checked, we're good
            }
        },
        [downloadCSV],
    );

    const handleDownloadCSVChange = useCallback(
        (e) => {
            const checked = e.target.checked;
            setDownloadCSV(checked);
            if (checked && !selectedTargetCloud) {
                // Will be validated on submit
            }
        },
        [selectedTargetCloud],
    );

    const validateForm = useCallback(() => {
        if (!selectedFile || sources.length === 0) {
            notifyError({ description: 'Please upload a valid CSV file with source data.' });
            return false;
        }

        if (downloadCSV && !selectedTargetCloud) {
            notifyError({ description: 'Target Cloud selection is required to download Migrations CSV.' });
            return false;
        }

        return true;
    }, [selectedFile, sources.length, downloadCSV, selectedTargetCloud, notifyError]);

    const handleSubmit = useCallback(async () => {
        if (!validateForm()) return;

        setLoading(true);
        setBulkErrors(null);

        try {
            // Prepare sources data
            const sourcesToSubmit = sources.map((source) => {
                const submissionData = { ...source };

                // Add appliance_id if target cloud is selected
                if (selectedTargetCloud) {
                    const selectedCloud = processedCloudAccounts.find((acc) => acc.id === selectedTargetCloud);
                    if (selectedCloud?.appliance_id) {
                        submissionData.appliance_id = selectedCloud.appliance_id;
                    }
                }

                return submissionData;
            });

            // Make API call to bulk create sources
            const result = await bulkCreateSources({
                sourcesData: sourcesToSubmit,
                updateExisting: overwriteExisting,
            }).unwrap();
            const sourceConfigIDs = result.content.map((savedConfig) => savedConfig.id);

            notifySuccess({ description: 'Source(s) saved successfully.' });

            // Prepare continuation payload for preflights if target cloud is selected
            let continuePayload = null;
            if (selectedTargetCloud) {
                const selectedCloud = processedCloudAccounts.find((acc) => acc.id === selectedTargetCloud);

                // Create preflight payload with source data for proper display
                const preflightPayload = sourceConfigIDs.map((id, index) => {
                    const sourceData = sources[index]; // Get corresponding source data from CSV
                    return {
                        type: 'source',
                        resource_id: id,
                        overrides: {},
                        sourceData: {
                            id: id,
                            host: sourceData.host || sourceData.source_ip,
                            hostname: sourceData.hostname || sourceData.host || sourceData.source_ip,
                            name: sourceData.hostname || sourceData.host || sourceData.source_ip || id,
                            os_type: sourceData.os_type,
                        },
                    };
                });

                // Add target cloud to preflight payload
                preflightPayload.push({
                    resource_id: selectedTargetCloud,
                    cloud_type: selectedCloud.cloud_type,
                    type: 'target_cloud',
                    sourceData: {
                        id: selectedTargetCloud,
                        name: selectedCloud.name || 'Target Cloud',
                        cloud_type: selectedCloud.cloud_type,
                    },
                });

                continuePayload = {
                    targetCloudID: selectedTargetCloud,
                    applianceID: selectedCloud.appliance_id,
                    cloudType: selectedCloud.cloud_type,
                    sourceConfigIDs,
                    downloadCSV,
                    preflightPayload,
                };
            }

            // Call success callback
            onSuccess?.(continuePayload);
            onClose();
        } catch (error) {
            console.error('Error uploading sources:', error);

            // Handle RTK Query errors
            if (error?.data) {
                setBulkErrors(error.data);

                // Show validation errors by default if they exist
                if (error.data.validation_errors) {
                    setShowAllErrors(true);
                    notifyError({
                        message: 'Validation Errors',
                        description: `Found ${error.data.validation_errors.length} validation error(s). Please review and fix the errors below.`,
                    });
                } else {
                    handleError(
                        error,
                        notifyError,
                        'Bulk upload has validation errors. Please fix the errors and try again.',
                    );
                }
            } else {
                notifyError({ description: 'Failed to upload sources. Please try again.' });
            }
        } finally {
            setLoading(false);
        }
    }, [
        validateForm,
        sources,
        selectedTargetCloud,
        overwriteExisting,
        processedCloudAccounts,
        downloadCSV,
        notifySuccess,
        notifyError,
        onSuccess,
        onClose,
        bulkCreateSources,
    ]);

    const handleCancel = useCallback(() => {
        // Reset all form state to initial values
        setSelectedFile(null);
        setFileName('');
        setSources([]);
        setErrors([]);
        setBulkErrors(null);
        setOverwriteExisting(false);
        setSelectedTargetCloud('');
        setDownloadCSV(false);
        setShowAllErrors(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onClose();
    }, [onClose]);

    // Reset values when modal opens/closes
    const handleModalClose = useCallback(() => {
        handleCancel();
    }, [handleCancel]);

    // Reset form when modal is opened
    useEffect(() => {
        if (open) {
            // Reset all form state when modal opens
            setSelectedFile(null);
            setFileName('');
            setSources([]);
            setErrors([]);
            setBulkErrors(null);
            setOverwriteExisting(false);
            setSelectedTargetCloud('');
            setDownloadCSV(false);
            setShowAllErrors(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [open]);

    // Error table columns for bulk errors
    const errorColumns = [
        {
            title: 'Source IP',
            dataIndex: 'source_ip',
            key: 'source_ip',
            width: '25%',
        },
        {
            title: 'Field',
            dataIndex: 'errors',
            key: 'field',
            width: '20%',
            render: (errors) => (
                <div>
                    {errors.map((error, index) => (
                        <div key={index} style={{ marginBottom: index < errors.length - 1 ? 4 : 0 }}>
                            <strong>{error.field}</strong>
                        </div>
                    ))}
                </div>
            ),
        },
        {
            title: 'Error Message',
            dataIndex: 'errors',
            key: 'errors',
            width: '55%',
            render: (errors) => (
                <div>
                    {errors.map((error, index) => (
                        <div key={index} style={{ color: '#ff4d4f', marginBottom: index < errors.length - 1 ? 4 : 0 }}>
                            {error.message}
                        </div>
                    ))}
                </div>
            ),
        },
    ];

    return (
        <Modal
            title="Upload Multiple Sources"
            open={open}
            onCancel={handleModalClose}
            footer={[
                <Button key="cancel" onClick={handleCancel} disabled={loading}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleSubmit}
                    disabled={!selectedFile || sources.length === 0 || (bulkErrors && !overwriteExisting)}
                >
                    {loading ? 'Saving Sources...' : 'Upload'}
                </Button>,
            ]}
            width={600}
            className="upload-multiple-sources-modal"
        >
            <Space orientation="vertical" style={{ width: '100%' }} size="middle">
                {/* Help Link */}
                <Row justify="end">
                    <Col>
                        <Link
                            href="https://docs.rivermeadow.com/add-multiple-sources"
                            target="_blank"
                            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                            <InfoCircleOutlined style={{ fontSize: 14, color: '#1890ff' }} />
                            How to Upload Multiple Sources
                        </Link>
                    </Col>
                </Row>

                {/* File Upload */}
                <Space.Compact>
                    <Button onClick={handleBrowseClick} disabled={loading}>
                        Browse
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                    <Input
                        value={fileName}
                        placeholder="No file selected"
                        readOnly
                        style={{ width: 'calc(100% - 75px)' }}
                        className="file-name-input"
                    />
                </Space.Compact>

                {/* Parse Errors */}
                {errors.length > 0 && (
                    <Alert
                        type="error"
                        title="CSV Parsing Errors"
                        description={
                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                                {errors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        }
                    />
                )}

                {/* Bulk Validation Errors */}
                {bulkErrors && (
                    <Space orientation="vertical" style={{ width: '100%' }}>
                        {bulkErrors.validation_errors && bulkErrors.validation_errors.length > 0 && (
                            <>
                                <Alert
                                    type="error"
                                    title={`Validation Errors (${bulkErrors.validation_errors.length})`}
                                    description="The following sources have validation errors and need to be corrected:"
                                    showIcon
                                    style={{ marginBottom: 8 }}
                                />
                                <Table
                                    dataSource={bulkErrors.validation_errors}
                                    columns={errorColumns}
                                    size="small"
                                    pagination={false}
                                    rowKey="source_ip"
                                    style={{ display: showAllErrors ? 'block' : 'none' }}
                                    bordered
                                />
                                <Button
                                    type="link"
                                    onClick={() => setShowAllErrors(!showAllErrors)}
                                    style={{ padding: 0 }}
                                >
                                    {showAllErrors ? 'Hide' : 'Show'} Validation Errors (
                                    {bulkErrors.validation_errors.length})
                                </Button>
                            </>
                        )}
                        {bulkErrors.message && <Alert type="error" title={bulkErrors.message} />}
                    </Space>
                )}

                {/* Overwrite Checkbox - Always visible as in screenshot */}
                <Checkbox
                    checked={overwriteExisting}
                    onChange={(e) => setOverwriteExisting(e.target.checked)}
                    disabled={loading}
                >
                    Overwrite existing source details
                </Checkbox>

                {/* Target Cloud Selection */}
                <TargetCloudSelector
                    processedCloudAccounts={processedCloudAccounts}
                    onChange={handleTargetCloudChange}
                    value={selectedTargetCloud || undefined}
                    loading={isLoadingCloudAccounts}
                    disabled={loading}
                    allowClear
                    useFormItem={false}
                    showTooltip={false}
                    showApplianceWarning={false}
                    label="If you wish to run preflight checks on uploaded sources, please select Target Cloud:"
                    placeholder="Select Target Cloud"
                    style={{ width: '100%' }}
                />
                {downloadCSV && !selectedTargetCloud && (
                    <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
                        Target Cloud selection is required to download Migrations CSV
                    </Text>
                )}

                {/* Download CSV Checkbox */}
                {selectedTargetCloud && (
                    <Checkbox checked={downloadCSV} onChange={handleDownloadCSVChange} disabled={loading}>
                        Download Migrations CSV upon completion.
                    </Checkbox>
                )}

                {/* Loading Indicator */}
                {loading && <Alert type="info" title="Saving Sources..." icon={<ExclamationCircleOutlined spin />} />}

                {/* Sources Preview */}
                {sources.length > 0 && (
                    <Alert
                        type="success"
                        title={`Successfully parsed ${sources.length} source${sources.length > 1 ? 's' : ''} from CSV`}
                    />
                )}
            </Space>
        </Modal>
    );
};

export default UploadMultipleSourcesModal;
